import "primeicons/primeicons.css";
import "./RTUList.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";

import { AppDispatch, RootState } from "../../redux/store/store";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Avatar } from "primereact/avatar";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { FilterMatchMode } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { OverlayPanel } from "primereact/overlaypanel";
import { RTU } from "../../types/RTU";
import RTUActions from "../RTUActions"; // Import the new component
import { SkeletonRegion, SkeletonTable } from "../common/Skeleton/Skeleton";
import { Tag } from "primereact/tag";
import { defaultThemeColor } from '../../config/themeConfig';
import { fetchRTUs } from "../../redux/slices/rtuSlice";
import { getEmptyMessage } from "../../utils/tableConfig";
import { buildAssetStatusFilterOptions, getStatusColor, getStatusLabelKey } from "../../utils/statusConfig";
import { humanizeStatusReason, humanizeStatusSource } from "../../utils/statusDisplayConfig";
import { PaginatorRowsLabel, ROWS_PER_PAGE_OPTIONS, STANDARD_PAGE_REPORT_TEMPLATE, STANDARD_PAGINATOR_TEMPLATE } from "../../utils/paginatorConfig";
import { formatDisplayDateTime } from "../../utils/dateTimeFormat";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const COLUMN_VISIBILITY_STORAGE_PREFIX = "dms_column_visibility";
const ASSET_LIST_VIEW_KEY = "asset-list";

const resolveColumnVisibilityUserKey = (): string => {
  if (typeof window === "undefined") {
    return "anonymous";
  }

  try {
    const rawUser = window.localStorage.getItem("dms_auth_user");
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as Record<string, unknown>;
      const stableIdentity =
        parsed.username ??
        parsed.logins ??
        parsed.email ??
        parsed.id ??
        parsed.employeeId;
      if (stableIdentity !== undefined && stableIdentity !== null) {
        return String(stableIdentity);
      }
    }
  } catch (error) {
    console.warn("Failed to parse dms_auth_user for column persistence key", error);
  }

  return "anonymous";
};

const getColumnVisibilityStorageKey = (viewKey: string): string =>
  `${COLUMN_VISIBILITY_STORAGE_PREFIX}:${viewKey}:${resolveColumnVisibilityUserKey()}`;

const hydrateColumnVisibility = <T extends { field: string; visible: boolean }>(
  columns: T[],
  storageKey: string
): T[] => {
  if (typeof window === "undefined") {
    return columns;
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return columns;
    }
    const parsed = JSON.parse(stored) as Record<string, boolean>;
    const hydrated = columns.map((column) =>
      Object.prototype.hasOwnProperty.call(parsed, column.field)
        ? { ...column, visible: Boolean(parsed[column.field]) }
        : column
    );
    if (hydrated.every((column) => !column.visible)) {
      console.warn("Stored column visibility hid every column; restoring defaults");
      return columns;
    }
    return hydrated;
  } catch (error) {
    console.warn("Failed to hydrate column visibility settings", error);
    return columns;
  }
};

const persistColumnVisibility = <T extends { field: string; visible: boolean }>(
  columns: T[],
  storageKey: string
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const payload = columns.reduce<Record<string, boolean>>((accumulator, column) => {
      accumulator[column.field] = Boolean(column.visible);
      return accumulator;
    }, {});
    window.localStorage.setItem(storageKey, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to persist column visibility settings", error);
  }
};

const RTUList: React.FC = () => {
  const { t } = useTranslation();
  const { rtus, loading } = useSelector((state: RootState) => state.rtu || {});
  const dispatch = useDispatch<AppDispatch>();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    location: { value: null, matchMode: FilterMatchMode.CONTAINS },
    diskUsage: { value: null, matchMode: FilterMatchMode.CONTAINS },
    id: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    cpuUsage: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastData: { value: null, matchMode: FilterMatchMode.EQUALS },
    statusObservedAtUtc: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastFileReceivedAtUtc: { value: null, matchMode: FilterMatchMode.EQUALS },
    configVersion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    deploymentVersion: { value: null, matchMode: FilterMatchMode.CONTAINS },
    installDate: { value: null, matchMode: FilterMatchMode.EQUALS },
    lastConfigUpdate: { value: null, matchMode: FilterMatchMode.EQUALS },
    publicIPAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    localIPAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    reportingToServer: { value: null, matchMode: FilterMatchMode.EQUALS },
    groupId: { value: null, matchMode: FilterMatchMode.EQUALS },
    provider: { value: null, matchMode: FilterMatchMode.CONTAINS },
    modemIP: { value: null, matchMode: FilterMatchMode.CONTAINS },
    ethernetIP: { value: null, matchMode: FilterMatchMode.CONTAINS },
    field: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [showFilters, setShowFilters] = useState(false);
  const columnStorageKeyRef = useRef<string>(getColumnVisibilityStorageKey(ASSET_LIST_VIEW_KEY));
  const hasHydratedColumnsRef = useRef(false);
  const toggleFilters = () => {
    setShowFilters((previous) => {
      const next = !previous;
      setColumns((currentColumns) =>
        currentColumns.map((column) => ({
          ...column,
          filter: next,
        }))
      );
      return next;
    });
  };
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<RTU[]>([]);
  const statusOptions = useMemo(() => buildAssetStatusFilterOptions(t), [t]);

  const tableRows = useMemo(
    () =>
      rtus.map((rtu) => ({
        ...rtu,
        searchText: [
          rtu.id,
          rtu.name,
          rtu.location,
          rtu.status,
          rtu.statusSource,
          rtu.statusReason,
          rtu.configVersion,
          rtu.deploymentVersion,
          rtu.publicIPAddress,
          rtu.localIPAddress,
          rtu.modemIP,
          rtu.ethernetIP,
          rtu.provider,
          rtu.field,
          rtu.groupId,
        ]
          .filter((value) => value !== undefined && value !== null)
          .join(" ")
          .toLowerCase(),
      })),
    [rtus]
  );

  const formatDateTime = (value: string | null | undefined) => formatDisplayDateTime(value);

  const statusTooltip = (rowData: RTU) => {
    const source = humanizeStatusSource(rowData.statusSource);
    const reason = rowData.statusReason ? ` - ${humanizeStatusReason(rowData.statusReason)}` : "";
    const statusLabel = t(getStatusLabelKey(rowData.status), { defaultValue: String(rowData.status ?? "") });

    return `${statusLabel} (${source})${reason}`;
  };

  const statusBodyTemplate = (rowData: RTU) => {
    const displayStatus = rowData.systemRestart === true ? "rebooting" : rowData.status;
    return (
      <span title={statusTooltip(rowData)} aria-label={statusTooltip(rowData)}>
        <Avatar
          style={{
            backgroundColor: getSeverity(displayStatus),
            width: "20px",
            height: "20px",
          }}
          shape="circle"
        />
      </span>
    );
  };

  const statusItemTemplate = (option: any) => {
    return (
      <Tag className="asset-status-filter-tag" value={option.label} style={{ backgroundColor: getSeverity(option.value) }} />
    );
  };

  const statusValueTemplate = (option: any) => {
    if (!option) {
      return <span />;
    }

    return (
      <Tag className="asset-status-filter-tag asset-status-filter-tag--value" value={option.label} style={{ backgroundColor: getSeverity(option.value) }} />
    );
  };

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        itemTemplate={statusItemTemplate}
        valueTemplate={statusValueTemplate}
        optionLabel="label"
        optionValue="value"
        placeholder=""
        className="p-column-filter asset-status-filter-dropdown"
        panelClassName="asset-status-filter-panel"
        showClear
        style={{ width: "100%", minWidth: "4rem" }}
      />
    );
  };

  const diskUsageBodyTemplate = (rowData: any) => {
    return `${rowData.diskUsage.toFixed(1)}%`;
  };

  const cpuUsageBodyTemplate = (rowData: any) => {
    return `${rowData.cpuUsage.toFixed(1)}%`;
  };

  const dateTemplate = (rowData: any) => formatDateTime(rowData.lastData);
  const installDateTemplate = (rowData: any) => formatDateTime(rowData.installDate);
  const statusObservedTemplate = (rowData: RTU) => formatDateTime(rowData.statusObservedAtUtc);
  const lastFileReceivedTemplate = (rowData: RTU) =>
    formatDateTime(rowData.lastFileReceivedAtUtc);
  const reportingToServerTemplate = (rowData: RTU) => formatDateTime(rowData.reportingToServer);
  const lastConfigUpdateTemplate = (rowData: RTU) => formatDateTime(rowData.lastConfigUpdate);

  const [columns, setColumns] = useState([
    {
      field: "status",
      header: t("assetList.columns.status"),
      headerClassName: "custom-header-status",
      visible: true,
      filterElement: statusRowFilterTemplate,
      body: statusBodyTemplate,
      filter: true,
    },
    {
      field: "id",
      header: t("assetList.columns.id"),
      headerClassName: "custom-header-id",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "name",
      header: t("assetList.columns.name"),
      headerClassName: "custom-header-name",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "location",
      header: t("assetList.columns.location"),
      headerClassName: "custom-header-location",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "configVersion",
      header: t("assetList.columns.configVersion"),
      headerClassName: "custom-header-config-version",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "deploymentVersion",
      header: t("assetList.columns.deploymentVersion"),
      headerClassName: "custom-header-deployment-version",
      visible: true,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "diskUsage",
      header: t("assetList.columns.diskUsage"),
      headerClassName: "custom-header-disk-usage",
      visible: true,
      filterElement: null,
      body: diskUsageBodyTemplate,
      filter: true,
    },
    {
      field: "cpuUsage",
      header: t("assetList.columns.cpuUsage"),
      headerClassName: "custom-header-cpu-usage",
      visible: true,
      filterElement: null,
      body: cpuUsageBodyTemplate,
      filter: true,
    },
    {
      field: "statusObservedAtUtc",
      header: t("assetList.columns.lastChecked", { defaultValue: "Last Checked" }),
      headerClassName: "custom-header-last-checked",
      visible: true,
      filterElement: null,
      body: statusObservedTemplate,
      filter: true,
    },
    {
      field: "lastFileReceivedAtUtc",
      header: t("assetList.columns.lastDataReceived", { defaultValue: "Last Data Received" }),
      headerClassName: "custom-header-last-data-received",
      visible: true,
      filterElement: null,
      body: lastFileReceivedTemplate,
      filter: true,
    },
    {
      field: "lastData",
      header: t("assetList.columns.lastData"),
      headerClassName: "custom-header-last-data",
      visible: false,
      filterElement: null,
      body: dateTemplate,
      filter: true,
    },
    {
      field: "installDate",
      header: t("assetList.columns.installDate"),
      headerClassName: "custom-header-install-date",
      visible: false,
      filterElement: null,
      body: installDateTemplate,
      filter: true,
    },
    {
      field: "lastConfigUpdate",
      header: t("assetList.columns.lastConfigUpdate"),
      headerClassName: "custom-header-last-config-update",
      visible: false,
      filterElement: null,
      body: lastConfigUpdateTemplate,
      filter: true,
    },
    {
      field: "publicIPAddress",
      header: t("assetList.columns.publicIp"),
      headerClassName: "custom-header-public-ipaddress",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "reportingToServer",
      header: t("assetList.columns.reportingToServer"),
      headerClassName: "custom-header-reportingtoserver",
      visible: false,
      filterElement: null,
      body: reportingToServerTemplate,
      filter: true,
    },
    {
      field: "groupId",
      header: t("assetList.columns.groupId"),
      headerClassName: "custom-header-group-id",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
    {
      field: "provider",
      header: t("assetList.columns.provider"),
      headerClassName: "custom-header-provider",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "modemIP",
      header: t("assetList.columns.modemIp"),
      headerClassName: "custom-header-modem-ip",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "ethernetIP",
      header: t("assetList.columns.ethernetIp"),
      headerClassName: "custom-header-ethernet-ip",
      visible: false,
      filterElement: null,
      body: null,
      expander: true,
      filter: true,
    },
    {
      field: "field",
      header: t("assetList.columns.field"),
      headerClassName: "custom-header-field",
      visible: false,
      filterElement: null,
      body: null,
      filter: true,
    },
  ]);

  const op: any = useRef(null);

  const onColumnsChange = useCallback((field: any, isChecked: any) => {
    setColumns((currentColumns) => {
      const visibleCount = currentColumns.filter((col) => col.visible).length;
      const isLastVisibleColumn =
        !isChecked && visibleCount === 1 && currentColumns.find((col) => col.field === field)?.visible;
      if (isLastVisibleColumn) {
        return currentColumns;
      }
      return currentColumns.map((col) => {
        if (col.field === field) {
          return { ...col, visible: isChecked };
        }
        return col;
      });
    });
  }, []);

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };
    //@ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", gap: "0.6rem", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flex: 1, minWidth: 0 }}>
          {showFilters && (
            <div style={{ display: 'flex', justifyContent: 'center', maxWidth: '200px' }}>
              <span className="p-input-icon-left" style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                <i className="pi pi-search" style={{ fontSize: "0.7rem", left: '0.5rem' }} />
                <InputText
                  value={globalFilterValue}
                  onChange={onGlobalFilterChange}
                  placeholder=""
                  style={{ fontSize: "0.75rem", height: "1.5rem", paddingLeft: "1.5rem", width: "100%" }}
                />
              </span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", justifyContent: "flex-end", marginLeft: "auto", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <i
              className="pi pi-search"
              style={{ cursor: "pointer", fontSize: "0.9rem", fontWeight: 700, color: showFilters ? defaultThemeColor : "#333", padding: "0.25rem" }}
              title={showFilters ? t('search.hide') : t('search.show')}
              onClick={toggleFilters}
            />
            <i
              className="pi pi-list"
              style={{ cursor: "pointer", fontSize: "0.9rem", fontWeight: 700, color: "#333", padding: "0.25rem" }}
              title={t('table.selectColumns')}
              onClick={(e) => op.current.toggle(e)}
            />
            <OverlayPanel ref={op} closeOnEscape dismissable={true} className="asset-column-picker">
              {columns.map((col) => (
                <div key={col.field} className="asset-column-picker__row">
                  <Checkbox
                    inputId={`cb-${col.field}`}
                    checked={col.visible}
                    onChange={(e) => onColumnsChange(col.field, e.checked)}
                    className="asset-column-picker__checkbox"
                  />
                  <label htmlFor={`cb-${col.field}`} className="asset-column-picker__label">{col.header}</label>
                </div>
              ))}
            </OverlayPanel>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", minWidth: "max-content" }}>
            <RTUActions mode="bulk" selectedRows={selectedRows} />
          </div>
        </div>
      </div>
    );
  };

  const header = renderHeader();

  const rowButtonTemplate = (rowData: RTU) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }} onClick={(event) => event.stopPropagation()}>
        <RTUActions mode="row" selectedRTUIp={rowData.publicIPAddress} selectedRows={[rowData]} />
      </div>
    );
  };

  const onSelectionChanged = useCallback((e: any) => {
    e.originalEvent?.stopPropagation?.();
    setSelectedRows(e.value ?? []);
  }, []);

  const onRowSelect = (event: any) => {
    if (event.originalEvent.target.type === "checkbox") {
      return;
    }
    const rtuId = event.data.id;
    const rtu = rtus.find((rtu) => rtu.id === rtuId);
    navigate(`/rtupage/${rtuId}`, { state: { rtu } });
  };

  const navigate = useNavigate();
  const getSeverity = (status: string) => {
    return getStatusColor(status);
  };

  useEffect(() => {
    dispatch(fetchRTUs({}));
  }, [dispatch]);

  useEffect(() => {
    setColumns((currentColumns) =>
      hydrateColumnVisibility(currentColumns, columnStorageKeyRef.current)
    );
  }, []);

  useEffect(() => {
    if (!hasHydratedColumnsRef.current) {
      hasHydratedColumnsRef.current = true;
      return;
    }
    persistColumnVisibility(columns, columnStorageKeyRef.current);
  }, [columns]);

  return (
    <div className="card" style={{ padding: '0.25rem', overflow: 'hidden' }}>
      <h1 className="dms-visually-hidden">{t('navigation.assetList')}</h1>
      <DataTable
        value={tableRows}
        className="small-datatable"
        size="small"
        {...(showFilters ? { filterDisplay: "row" } : {})}
        filters={filters}
        globalFilterFields={[
          "searchText",
          "id",
          "name",
          "location",
          "status",
          "statusSource",
          "statusReason",
          "configVersion",
          "deploymentVersion",
          "publicIPAddress",
          "localIPAddress",
          "modemIP",
          "ethernetIP",
          "provider",
          "field",
          "groupId",
        ]}
        header={header}
        sortMode="multiple"
        removableSort
        selectionMode={null}
        selection={selectedRows}
        onSelectionChange={onSelectionChanged}
        scrollable
        paginator
        rows={25}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        paginatorLeft={<PaginatorRowsLabel />}
        paginatorTemplate={STANDARD_PAGINATOR_TEMPLATE}
        currentPageReportTemplate={STANDARD_PAGE_REPORT_TEMPLATE}
        paginatorClassName="compact-paginator"
        dataKey="id"
        tableStyle={{ tableLayout: 'fixed', width: '100%' }}
        emptyMessage={loading ? (
          <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
            <SkeletonTable columns={[0.6, 0.6, 1, 1.4, 1, 1.2, 0.8, 0.8, 1.2, 1.3]} rows={10} showHeader={false} />
          </SkeletonRegion>
        ) : getEmptyMessage(t)}
        onRowSelect={onRowSelect}
        showGridlines
      >
        <Column
          selectionMode="multiple"
          style={{ width: "3rem" }}
          headerStyle={{ width: "3rem" }}
        ></Column>
        {columns
          .filter((col) => col.visible)
          .map((col) => {
            // Columns that should be centered (numeric, percentage, status, icons)
            const centerCols = ['status', 'id', 'diskUsage', 'cpuUsage', 'groupId'];
            const isCenter = centerCols.includes(col.field);
            
            // Fixed column widths for consistency between search/no-search modes
            const columnWidths: { [key: string]: string } = {
              status: '70px',
              id: '70px',
              name: '100px',
              location: '180px',
              configVersion: '120px',
              deploymentVersion: '150px',
              diskUsage: '100px',
              cpuUsage: '100px',
              statusObservedAtUtc: '150px',
              lastFileReceivedAtUtc: '170px',
              lastData: '120px',
              installDate: '120px',
              lastConfigUpdate: '150px',
              publicIPAddress: '130px',
              reportingToServer: '140px',
              groupId: '80px',
              provider: '120px',
              modemIP: '120px',
              ethernetIP: '120px',
              field: '100px',
            };
            
            return (
              <Column
                key={col.field}
                field={col.field}
                filter={showFilters}
                showFilterMenu={false}
                sortable
                headerClassName={col.headerClassName}
                header={col.header}
                style={{ width: columnWidths[col.field] || '100px' }}
                bodyStyle={{ textAlign: isCenter ? "center" : "left" }}
                filterElement={col.filterElement}
                body={col.body}
              />
            );
          })}
        <Column
          body={rowButtonTemplate}
          filter={showFilters}
                    frozen
          alignFrozen="right"
          filterElement={() => <span></span>}
                    header={t('table.actions')} 
          style={{ width: '148px' }}
          headerStyle={{ textAlign: 'center' }}
          bodyStyle={{ textAlign: 'center', overflow: 'visible' }}
        ></Column>
      </DataTable>
    </div>
  );
};

export default RTUList;
